import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { callFunction } from '../firebase';
import { TENANT_ID, TENANT_COMPANY_NAME } from '../tenant';
import { clearClientCreds, readClientCreds, splitPolicyBlock } from '../clientSession';
import { useI18n, usePublicTenantLocale } from '../i18n';
import ClientPortalLayout from '../components/ClientPortalLayout';
import InsuranceAttestation from '../components/InsuranceAttestation';
import { isMotorPolicy, openMotorCertificatePrint, printPayloadFromPolicy } from '../pulsePrint';
import { formatMsisdn, PULSE_PAY_DISPLAY, payInstruction, splitPremium, walletPayNumber } from '../wallets';
import { defaultPaymentMethod, moduleOn, useDirectoryFeatures } from '../tenantFeatures';
import { fileToPhotoPayload } from '../claims';
import '../styles/attestation.css';
import '../styles/client-portal.css';

function ClientPortal() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const [localeTenant, setLocaleTenant] = useState(TENANT_ID);
    usePublicTenantLocale(localeTenant);
    const features = useDirectoryFeatures(localeTenant);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [fnol, setFnol] = useState({ policyId: '', description: '', incidentDate: '' });
    const [fnolFile, setFnolFile] = useState(null);
    const [fnolBusy, setFnolBusy] = useState(false);
    const [fnolMsg, setFnolMsg] = useState('');

    useEffect(() => {
        const creds = readClientCreds();
        if (!creds) {
            navigate('/client/login', { replace: true });
            return;
        }
        callFunction('lookupMyPolicy', { phone: creds.phone, pin: creds.pin, tenantSlug: TENANT_ID })
            .then((data) => {
                const rows = data.results || [];
                setResults(rows);
                if (rows[0] && rows[0].tenantId) setLocaleTenant(rows[0].tenantId);
                setLoading(false);
            })
            .catch((err) => {
                clearClientCreds();
                setError(err.message || 'Your session expired. Sign in again.');
                setLoading(false);
                navigate('/client/login', { replace: true });
            });
    }, [navigate]);

    if (loading) {
        return (
            <div className="att-shell">
                <div className="alert alert-light">{t('client.opening')}</div>
            </div>
        );
    }

    const first = results[0] || {};
    const customer = first.customer || {};
    const companyName = first.companyName || TENANT_COMPANY_NAME;
    const logoUrl = first.logoUrl || '';

    return (
        <ClientPortalLayout companyName={companyName} logoUrl={logoUrl} customer={customer}>
            <style>{`
                @media print { body { background:#fff !important; } .no-print { display:none !important; } }
            `}</style>
            {error && <div className="alert alert-danger no-print">{error}</div>}
            <div className="cp-welcome no-print">
                <h1>{t('client.welcome', { name: customer.fullName ? ', ' + customer.fullName.split(' ')[0] : '' })}</h1>
                <p>{t('client.portalBlurb', { company: companyName })}</p>
                <div className="cp-meta">
                    {customer.phone ? <span>{customer.phone}</span> : null}
                    {customer.nationalId ? <span>ID {customer.nationalId}</span> : null}
                </div>
            </div>

            {results.map((block, i) => {
                const { cards, pending } = splitPolicyBlock(block);
                return (
                    <div key={i}>
                        {cards.length === 0 && pending.length === 0 && (
                            <div className="alert alert-warning no-print">{t('client.noPolicies')}</div>
                        )}
                        {cards.map((p) => (
                            <React.Fragment key={p.id}>
                                <InsuranceAttestation
                                    companyName={block.companyName}
                                    logoUrl={block.logoUrl}
                                    customer={block.customer}
                                    policy={p}
                                    tenantId={block.tenantId || TENANT_ID}
                                />
                                {String(p.paymentStatus || '').toLowerCase() === 'unpaid' ? (
                                    <div className="alert alert-warning no-print">
                                        {payInstruction({
                                            amount: splitPremium(p).grossPremium,
                                            method: p.paymentMethod || defaultPaymentMethod(),
                                            number: formatMsisdn(walletPayNumber(null, p.paymentMethod) || PULSE_PAY_DISPLAY),
                                            policyNumber: p.policyNumber,
                                            name: block.customer.fullName,
                                            companyName: block.companyName || companyName
                                        })}
                                    </div>
                                ) : null}
                            </React.Fragment>
                        ))}
                        {cards.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline-light w-100 mb-3 no-print"
                                onClick={() => {
                                    const motor = cards.filter(isMotorPolicy).map((p) => printPayloadFromPolicy(p, block.customer));
                                    if (motor.length) openMotorCertificatePrint({ certificates: motor });
                                    else window.print();
                                }}
                            >
                                {t('client.savePrint')}
                            </button>
                        )}
                        {pending.length > 0 && (
                            <div className="alert alert-info no-print">
                                {t('client.pending', { count: pending.length, company: block.companyName })}
                            </div>
                        )}
                    </div>
                );
            })}

            {results.length > 0 && moduleOn(features, 'claimsFnol') && (
                <form
                    id="fnol"
                    className="card border-0 shadow-sm mb-4 no-print"
                    onSubmit={async (e) => {
                        e.preventDefault();
                        const creds = readClientCreds();
                        if (!creds) return;
                        setFnolBusy(true);
                        setFnolMsg('');
                        try {
                            let photos = [];
                            if (fnolFile) {
                                try {
                                    const payload = await fileToPhotoPayload(fnolFile);
                                    if (payload) photos = [payload];
                                } catch (err) {
                                    setFnolMsg(err.message || t('client.fnolPhotoSkip'));
                                }
                            }
                            const row = await callFunction('submitFnol', {
                                phone: creds.phone,
                                pin: creds.pin,
                                tenantSlug: creds.tenantSlug,
                                policyId: fnol.policyId,
                                incidentDate: fnol.incidentDate,
                                incidentDescription: fnol.description,
                                photos
                            });
                            setFnolMsg(t('client.fnolFiled', { num: (row && row.claimNumber) || '' }));
                            setFnol({ policyId: '', description: '', incidentDate: '' });
                            setFnolFile(null);
                        } catch (err) {
                            setFnolMsg(err.message || t('client.fnolFailed'));
                        }
                        setFnolBusy(false);
                    }}
                >
                    <div className="card-body">
                        <h5>{t('client.fnolTitle')}</h5>
                        <p className="small text-muted">{t('client.fnolBlurb')}</p>
                        {fnolMsg && <div className="alert alert-info">{fnolMsg}</div>}
                        <label className="form-label">{t('client.fnolPolicy')}</label>
                        <select className="form-select mb-2" required value={fnol.policyId} onChange={(e) => setFnol({ ...fnol, policyId: e.target.value })}>
                            <option value="">{t('client.fnolPick')}</option>
                            {results.flatMap((block) => (block.policies || []).map((p) => (
                                <option key={p.id} value={p.id}>{(p.policyNumber || p.id) + ' · ' + (p.productName || p.policyType || '')}</option>
                            )))}
                        </select>
                        <label className="form-label">{t('claims.incidentDate')}</label>
                        <input type="date" className="form-control mb-2" value={fnol.incidentDate} onChange={(e) => setFnol({ ...fnol, incidentDate: e.target.value })} />
                        <label className="form-label">{t('client.fnolDesc')}</label>
                        <textarea className="form-control mb-2" rows={3} required value={fnol.description} onChange={(e) => setFnol({ ...fnol, description: e.target.value })} />
                        <label className="form-label">{t('client.fnolPhoto')}</label>
                        <input type="file" accept="image/*" className="form-control mb-3" onChange={(e) => setFnolFile((e.target.files && e.target.files[0]) || null)} />
                        <button type="submit" className="btn btn-warning" disabled={fnolBusy}>{fnolBusy ? t('common.loading') : t('client.fnolSubmit')}</button>
                    </div>
                </form>
            )}
        </ClientPortalLayout>
    );
}

export default ClientPortal;
